import { ReactElement, ReactNode, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { WidgetEntryDimensions, logAnalyticsEvent, noopFunction } from '@vestico/utils';

import {
  WidgetButtonEntry,
  WidgetContentEntry,
  WidgetEntry,
  WidgetEntryLocation,
  WidgetEntryType,
  WidgetImagePreviewEntry,
  WidgetLinkEntry,
  displayFilterIsMatching,
} from '@vestico/model';

import { ProductContext } from './MainContext';
import { getStaticEntryElements, hasStaticEntryElement } from './WidgetEntryContextStaticElements';
import { ConfigContext } from './WidgetEventsManager';
import { ContentDisplayLazyLoader } from './layout/contentDisplay/ContentDisplayLazyLoader';
import { ButtonInjectionLazy } from './layout/injections/ButtonInjectionLazy';
import { ImagePreviewInjectionLazy } from './layout/injections/ImagePreviewInjectionLazy';
import { LinkInjectionLazy } from './layout/injections/LinkInjectionLazy';
import { useShowEntry } from './layout/injections/UseShowEntry';

const entryLocationIsValid = (entry: WidgetEntry, hasProduct: boolean, hasCollection: boolean, isQuiz: boolean): boolean => {
  const { location, displayFilter } = entry;
  const { pathname, href } = window.location;
  const map: { [key in WidgetEntryLocation]: boolean } = {
    all: true,
    home: pathname === '/',
    pdps: hasProduct,
    plps: hasCollection,
  };

  const matchesLocation = map[location];
  if (!matchesLocation && !isQuiz) {
    return false;
  }

  const sanitizedURL = href.length > 1 && href.endsWith('/') ? href.slice(0, -1) : href;
  return displayFilterIsMatching(displayFilter, sanitizedURL);
};

const entryTypeRequiresMainProduct = (entryType: WidgetEntryType): boolean => {
  const map: { [key in WidgetEntryType]: boolean } = {
    button: true,
    imagePreview: true,
    link: true,
    content: false,
  };
  return map[entryType];
};

const injectionScriptIsValid = (script: string) => {
  return script.includes('resolve(');
};

export interface WidgetEntryContextProps {
  entry?: WidgetEntry;
}

type SingleElementInjectionScriptResponse = Element | null | undefined;
type MultiElementInjectionScriptResponse = Element[] | Element | undefined;

export const WidgetEntryContext = ({ entry }: WidgetEntryContextProps) => {
  const [didInjectPreview, setDidInjectPreview] = useState(false);
  const { product } = useContext(ProductContext);
  const { isSDK, isQuiz, clientId, productCollection } = useContext(ConfigContext);

  const hasStaticElement = useMemo(() => entry && hasStaticEntryElement(entry), [entry]);

  const [renderItems, setRenderItems] = useState<ReactNode[] | undefined>(undefined);
  const [entryPointRootElement, setEntryPointRootElement] = useState<Element | undefined | null>(undefined);
  const [entryPointRenderElements, setEntryPointRenderElements] = useState<Element[]>([]);

  const { showEntry: intermediateShowEntry } = useShowEntry({ entry });
  const showEntry = useMemo(() => intermediateShowEntry || !!isQuiz, [intermediateShowEntry, isQuiz]);

  useLayoutEffect(() => {
    // Don't remove entry points from the theme is they're theme inserted
    if (entry?.isThemeInserted) {
      return noopFunction;
    }
    return () => {
      for (const renderElement of entryPointRenderElements) {
        renderElement.remove();
      }
      entryPointRootElement?.remove();
    };
  }, [entry?.isThemeInserted, entryPointRenderElements, entryPointRootElement]);

  const createRenderItem = useCallback(
    (newEntryPointRenderElement: HTMLElement) => {
      const entryType = entry?.type;
      let newRenderItem: ReactNode;
      switch (entryType) {
        case WidgetEntryType.imagePreview:
          newRenderItem = <ImagePreviewInjectionLazy entry={entry as WidgetImagePreviewEntry} />;
          break;
        case WidgetEntryType.link:
          newRenderItem = <LinkInjectionLazy entry={entry as WidgetLinkEntry} />;
          break;
        case WidgetEntryType.button:
          newRenderItem = <ButtonInjectionLazy entry={entry as WidgetButtonEntry} element={newEntryPointRenderElement} />;
          break;
        case WidgetEntryType.content:
          newRenderItem = <ContentDisplayLazyLoader entry={entry as WidgetContentEntry} />;
          break;
        default:
          throw new Error(`Unknown entry type ${entryType}`);
      }

      if (!newRenderItem) {
        throw new Error(`Missing new render item for entry ${entryType}`);
      }
      return createPortal(newRenderItem, newEntryPointRenderElement);
    },
    [entry]
  );

  const getEntryPointElements = useCallback(
    async (
      entry: WidgetEntry
    ): Promise<[renderElements: SingleElementInjectionScriptResponse[] | null | undefined, rootElement: SingleElementInjectionScriptResponse]> => {
      const { injectionScript } = entry;

      const waitFn = (ev: string) => {
        return new Promise((resolve) => {
          // eslint-disable-next-line no-new-func
          new Function('resolve', ev)(resolve);
        });
      };

      // The root, if available, will contain other elements including the render element
      let newEntryPointRootElement: Element | null | undefined;
      let newEntryPointRenderElements: (Element | null | undefined)[] | undefined | null;

      if (hasStaticElement) {
        const staticEntryElements = getStaticEntryElements(entry, isSDK);
        newEntryPointRenderElements = staticEntryElements;
      } else {
        const injectionScriptResult = (await waitFn(injectionScript)) as
          | SingleElementInjectionScriptResponse
          | [SingleElementInjectionScriptResponse | MultiElementInjectionScriptResponse, SingleElementInjectionScriptResponse];
        if (injectionScriptResult && Array.isArray(injectionScriptResult)) {
          const [elementResult] = injectionScriptResult;
          if (Array.isArray(elementResult)) {
            newEntryPointRenderElements = elementResult;
          } else if (elementResult) {
            newEntryPointRenderElements = [elementResult];
          }

          [, newEntryPointRootElement] = injectionScriptResult;
        } else {
          newEntryPointRenderElements = [injectionScriptResult];
        }
      }
      return [newEntryPointRenderElements, newEntryPointRootElement];
    },
    [hasStaticElement, isSDK]
  );

  const shouldLoadEntryPoints = useMemo(() => {
    if (!entry) {
      console.debug('Dont inject - entry');
      return false;
    }
    const { injectionScript, enabled, type: entryType, location } = entry;

    if (!enabled) {
      console.debug(`Inject - not enabled: ${entry.id}`);
      return false;
    }

    if (!showEntry && !isQuiz) {
      console.debug(`Entry type ${entryType} has not enough posts: ${entry.id}`);
      return false;
    }

    if (!entryLocationIsValid(entry, !!product, !!productCollection, !!isQuiz)) {
      console.debug(`Entry location not valid: ${entry.id} ${location}`);
      return false;
    }

    const requiresMainProductForEntry = entryTypeRequiresMainProduct(entryType);

    if (requiresMainProductForEntry && !product && !isQuiz) {
      console.debug(`Entry type requires product, but we have none: ${entry.id}`);
      return false;
    }

    const injectionScriptValid = hasStaticElement || (!hasStaticElement && injectionScriptIsValid(injectionScript));
    if (!injectionScriptValid) {
      console.debug(`Injection script not valid, ignoring ${entry.id}`);
      return false;
    }

    return true;
  }, [entry, hasStaticElement, product, productCollection, showEntry, isQuiz]);

  const injectEntryPoint = useCallback(async () => {
    if (!shouldLoadEntryPoints || !entry) {
      return;
    }

    console.debug(`Injecting entry ${entry.id}`);
    setDidInjectPreview(true);

    try {
      const [newEntryPointRenderElements, newEntryPointRootElement] = await getEntryPointElements(entry);

      const renderItemPortals: ReactElement[] = [];
      if (!newEntryPointRenderElements) {
        throw new Error('No entry point elements');
      }

      const finalEntryPointsRenderElements: Element[] = [];
      for (const newEntryPointRenderElement of newEntryPointRenderElements) {
        if (!newEntryPointRenderElement || !(newEntryPointRenderElement instanceof HTMLElement)) {
          if (!hasStaticElement) {
            console.error(`failed to insert parent element ${newEntryPointRenderElement}. id ${entry.id}`);
            logAnalyticsEvent({
              category: 'exception',
              event: 'failed_insert_parent_element',
              dimensions: { provider: clientId, entry_id: entry.id } as WidgetEntryDimensions,
            });
          }
          continue;
        }

        finalEntryPointsRenderElements.push(newEntryPointRenderElement);
        const renderItem = createRenderItem(newEntryPointRenderElement);
        renderItemPortals.push(renderItem);
      }

      console.debug(`Rendering entry point ${entry.id}: ${entry.type} ${finalEntryPointsRenderElements.length} elements`);
      // set render item through portal
      setEntryPointRootElement(newEntryPointRootElement);
      setEntryPointRenderElements(finalEntryPointsRenderElements);
      setRenderItems(renderItemPortals);
    } catch (error) {
      console.error(`Injecting html: ${error}`);
      logAnalyticsEvent({
        category: 'exception',
        event: 'failed_insert_entry_element',
        name: error.message,
        dimensions: { provider: clientId, entry_id: entry.id } as WidgetEntryDimensions,
      });
    }
  }, [shouldLoadEntryPoints, entry, getEntryPointElements, createRenderItem, hasStaticElement, clientId]);

  useLayoutEffect(() => {
    setDidInjectPreview(false);
  }, [entry]);

  useLayoutEffect(() => {
    if (!entry || didInjectPreview) {
      return;
    }

    injectEntryPoint();
  }, [entry, injectEntryPoint, didInjectPreview]);

  if (showEntry && renderItems && renderItems.length > 0) {
    return <>{renderItems}</>;
  }

  return null;
};
